import React from 'react';
import { Brain, Code, Rocket, Users, TrendingUp, Globe, MessageCircle, Target, Sparkles, Cpu, GraduationCap, Shield } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const Skills = () => {
  const skillCategories = [
    {
      category: 'Transformational Leadership',
      icon: Target,
      skills: [
        'Coaching & Personal Development',
        'Team Building & Facilitation',
        'High-Impact Communication',
        'Leadership Development'
      ]
    },
    {
      category: 'AI & Digital Innovation',
      icon: Cpu,
      skills: [
        'AI-Assisted Learning Systems',
        'Ethical AI Governance',
        'Creative Systems Design',
        'AI Content & Avatar Creation'
      ]
    },
    {
      category: 'Business & Marketing',
      icon: TrendingUp,
      skills: [
        'Sales & Business Development',
        'Digital Marketing Strategy',
        'Affiliate Marketing',
        'Brand Storytelling'
      ]
    },
    {
      category: 'Global Impact & Education',
      icon: GraduationCap,
      skills: [
        'E-Learning Platform Development',
        'Cross-Cultural Communication',
        'Youth Empowerment Initiatives',
        'Peace Movement Strategy'
      ]
    }
  ];

  return (
    <section id="skills" className="py-24 bg-gradient-to-b from-[#0B1D2A] to-black relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-72 h-72 bg-[#D4AF37]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Core <span className="text-[#D4AF37]">Competencies</span>
          </h2>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto rounded-full mb-6" />
          <p
            className="text-lg text-white/70 max-w-2xl mx-auto"
            style={{ fontFamily: 'Lato, sans-serif' }}
          >
            A unique blend of transformational leadership, AI innovation, and global impact expertise
          </p>
        </div>

        {/* Skills Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {skillCategories.map((category, index) => (
            <Card
              key={index}
              className="bg-[#0B1D2A]/60 border-[#D4AF37]/20 p-8 hover:border-[#D4AF37] transition-all duration-300 hover:transform hover:scale-105 backdrop-blur-sm group"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                  <category.icon className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3
                  className="text-2xl font-bold text-white flex-1"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {category.category}
                </h3>
              </div>
              <div className="space-y-3">
                {category.skills.map((skill, skillIndex) => (
                  <div
                    key={skillIndex}
                    className="flex items-center gap-3 text-white/80 hover:text-[#D4AF37] transition-colors group/skill"
                  >
                    <div className="w-2 h-2 rounded-full bg-[#D4AF37] group-hover/skill:scale-150 transition-transform" />
                    <span
                      className="text-base"
                      style={{ fontFamily: 'Lato, sans-serif' }}
                    >
                      {skill}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>

        {/* Featured Skills */}
        <div className="mt-16 text-center">
          <p
            className="text-sm text-white/60 mb-6"
            style={{ fontFamily: 'Lato, sans-serif' }}
          >
            Core Technologies & Methodologies
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'AI Ethics',
              'Conscious Leadership',
              'Digital Transformation',
              'Global Education',
              'Peace Advocacy',
              'Human Potential',
              'Systems Thinking',
              'Creative Innovation',
              'Cross-Cultural Impact'
            ].map((tech, index) => (
              <Badge
                key={index}
                className="bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37] hover:text-black transition-all duration-300 px-4 py-2 text-sm"
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                {tech}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
